import { Suspense, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import { buildCallUrl, buildDemoUrl, getXoDemoMode, nextXoLang, toggleLangUrl, xoDemoModeParams } from "../lib/xoDemoContext";
import { getXoLang, xoCopy } from "../lib/xoCopy";
import { XOPublicQuietLoading } from "./XOPublicQuietLoading";
import { XOPublicRouteErrorBoundary } from "./XOPublicRouteErrorBoundary";

export function isXoDemoSurfacePath(pathname: string): boolean {
  return pathname === "/demo"
    || pathname === "/call"
    || pathname === "/call-continuity-demo"
    || pathname.startsWith("/call/")
    || pathname === "/record"
    || pathname.startsWith("/record/")
    || pathname.startsWith("/xo-proof/material/")
    || pathname === "/trust-loop";
}

function XODemoSurfaceTopNav() {
  const location = useLocation();
  const lang = getXoLang(location.search);
  const copy = xoCopy(lang);
  const isDemoMode = getXoDemoMode(location.search);
  const isRecordDemoMode = isDemoMode && (location.pathname === "/record" || location.pathname.startsWith("/record/"));
  const callStartsDemoMode = location.pathname === "/demo" || isDemoMode;
  const toggleHref = toggleLangUrl(`${location.pathname}${location.search}${location.hash}`, nextXoLang(lang));
  const currentParams = new URLSearchParams(location.search);
  const preservedParams = Object.fromEntries(currentParams.entries()) as Record<string, string>;
  if (isDemoMode) preservedParams.demo = "1";
  const demoHref = buildDemoUrl(lang, { params: preservedParams });
  const callHref = buildCallUrl(lang, { params: { ...preservedParams, ...xoDemoModeParams(callStartsDemoMode) } });

  if (isRecordDemoMode) {
    return (
      <nav
        aria-label="Record navigation"
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 10,
          marginBottom: "clamp(18px, 3vw, 28px)",
          fontSize: 12,
          fontWeight: 760,
          color: "rgba(246,240,230,0.74)",
        }}
      >
        <Link to={toggleHref} style={demoNavLinkStyle}>{copy.languageToggle}</Link>
        <span aria-hidden="true">·</span>
        <span>Record active</span>
        <span aria-hidden="true">·</span>
        <span>Agreement stays open</span>
      </nav>
    );
  }

  return (
    <nav
      aria-label="Navigation"
      style={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: 10,
        marginBottom: "clamp(22px, 4vw, 34px)",
        fontSize: 12,
        fontWeight: 760,
      }}
    >
      <Link to={toggleHref} style={demoNavLinkStyle}>{copy.languageToggle}</Link>
      <Link to={demoHref} style={demoNavLinkStyle}>Example</Link>
      <Link to={callHref} style={demoNavCtaStyle}>{copy.demo.primaryCta}</Link>
    </nav>
  );
}

const demoNavLinkStyle = {
  color: "rgba(246,240,230,0.7)",
  textDecoration: "none",
} as const;

const demoNavCtaStyle = {
  color: "#1E1B16",
  background: "#F6F0E6",
  border: "1px solid rgba(246,240,230,0.72)",
  borderRadius: 999,
  padding: "7px 11px",
  textDecoration: "none",
  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
} as const;

export function XODemoSurfaceShell({
  children,
  loadingLabel = "Loading…",
}: {
  children: ReactNode;
  loadingLabel?: string;
}) {
  return (
    <div
      className="xo-demo-surface-mode"
      style={{
        position: "relative",
        width: "100%",
        flex: 1,
        minWidth: 0,
        minHeight: "100dvh",
        overflowX: "hidden",
        background: "radial-gradient(circle at top, rgba(200,164,77,0.15), transparent 34%), #1E1B16",
        color: "#F6F0E6",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          width: "min(900px, calc(100% - clamp(28px, 7vw, 84px)))",
          marginInline: "auto",
          padding: "clamp(24px, 5vw, 44px) 0 clamp(54px, 9vw, 90px)",
          boxSizing: "border-box",
        }}
      >
        <XODemoSurfaceTopNav />
        <XOPublicRouteErrorBoundary>
          <Suspense fallback={<XOPublicQuietLoading label={loadingLabel} />}>
            {children}
          </Suspense>
        </XOPublicRouteErrorBoundary>
      </div>
    </div>
  );
}
